import React from 'react'
import { CSSObject } from '@emotion/react'
import {
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  Heading3,
  Icon,
  Image,
  Overflow,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

import { findDietaryClaimIcon } from './dietaryClaim'

type CWCCookedByYouRecipeCardProps = {
  recipe: Recipe
  isNewAndLimitedEdition: boolean
}

const CardRoot = withCitrusTheme(Box, (theme) => [
  {
    backgroundColor: theme.color(Color.White),
    borderRadius: '8px',
    boxShadow: '0px 1px 4px 0px rgba(33, 41, 48, 0.1), 0px 0px 2px 0px rgba(33, 41, 48, 0.05)',
    overflow: 'hidden',
    width: '320px',
    height: '346px',
  } as CSSObject,
  theme.responsiveSpacing(3, ['marginRight']) as CSSObject,
])

const ImageWrapper = withCitrusTheme(Box, () => ({
  position: 'relative',
  height: '228px',
}))

const DietaryIcon = withCitrusTheme(Box, () => ({
  position: 'absolute',
  top: '16px',
  left: '16px',
  height: '28px',
  width: '28px',
}))

// Straddles the image/info-panel seam — vertically centered on the boundary line, half the
// badge's 24px height overlapping each side, per design feedback.
const TagsRow = withCitrusTheme(Box, (theme) => [
  {
    position: 'absolute',
    left: '16px',
    bottom: '-12px',
    display: 'flex',
    alignItems: 'center',
  } as CSSObject,
  theme.responsiveSpacing(2, ['gap']) as CSSObject,
])

const NewPill = withCitrusTheme(Box, (theme) => [
  {
    backgroundColor: theme.color(Color.Success_600),
    color: theme.color(Color.White),
    borderRadius: '4px',
    fontFamily: theme.tokens.FontFamilies.book,
  } as CSSObject,
  theme.responsiveFont(2) as CSSObject,
  theme.responsiveSpacing(0.5, ['paddingTop', 'paddingBottom']) as CSSObject,
  theme.responsiveSpacing(2, ['paddingLeft', 'paddingRight']) as CSSObject,
])

const TagPill = withCitrusTheme(Box, (theme) => [
  {
    backgroundColor: theme.color(Color.NeutralGrey_50),
    color: theme.color(Color.ColdGrey_800),
    borderRadius: '4px',
    fontFamily: theme.tokens.FontFamilies.book,
  } as CSSObject,
  theme.responsiveFont(2) as CSSObject,
  theme.responsiveSpacing(0.5, ['paddingTop', 'paddingBottom']) as CSSObject,
  theme.responsiveSpacing(2, ['paddingLeft', 'paddingRight']) as CSSObject,
])

// Fixed to a 2-line height so the details row below always starts at the same position,
// whether the recipe name wraps to 1 line or 2 — matches Figma, where the title box height is
// fixed regardless of the actual rendered title length.
const TitleWrapper = withCitrusTheme(Box, (theme) => [
  {
    overflow: 'hidden',
  } as CSSObject,
  theme.responsiveSpacing(12, ['height']) as CSSObject,
])

export const CWCCookedByYouRecipeCard = ({
  recipe,
  isNewAndLimitedEdition,
}: CWCCookedByYouRecipeCardProps) => {
  const dietaryClaimIcon = findDietaryClaimIcon(recipe)
  const tagline = recipe.merchandisingTags?.tagline?.name

  return (
    <CardRoot data-testid="cooked-by-you-card">
      <ImageWrapper>
        <Image
          alt={recipe.name}
          src={recipe.images?.[0]?.crops?.[2]?.url}
          fit="cover"
          height="228px"
          width="100%"
        />
        {dietaryClaimIcon && (
          <DietaryIcon data-testid="dietary-claim">
            <Icon name={dietaryClaimIcon} size={8} />
          </DietaryIcon>
        )}
        {(isNewAndLimitedEdition || tagline) && (
          <TagsRow>
            {isNewAndLimitedEdition && <NewPill>New</NewPill>}
            {tagline && <TagPill>{tagline}</TagPill>}
          </TagsRow>
        )}
      </ImageWrapper>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
        paddingV={4}
        paddingH={4}
      >
        <TitleWrapper>
          <Heading3 size={4}>{recipe.name}</Heading3>
        </TitleWrapper>
        <Box display={Display.Flex} flexDirection={FlexDirection.Row} overflowY={Overflow.Hidden}>
          <Body2 color={Color.ColdGrey_600} size={2}>
            {recipe.prepTime} mins | {recipe.nutritionalInformation.perPortion.energyKcal} kcal |{' '}
            {Math.round(recipe.nutritionalInformation.perPortion.proteinMg / 1000)}g protein
          </Body2>
        </Box>
      </Box>
    </CardRoot>
  )
}
